<template>
  <div class="sc">
    <div class="sc-section-head">
      <h2>Consent Forms</h2>
      <p class="sc-help">
        Map procedure codes to the consent forms a patient should fill out before their visit.
        This mapping is used in two places:
      </p>
      <ul class="sc-help">
        <li>
          <strong>Treatment plans</strong> — sending a plan with the
          <strong>"Also send consent forms"</strong> checkbox enabled surfaces a
          "Consent forms to fill out before your appointment" section in the patient view,
          listing only the forms that match the codes on that plan.
        </li>
        <li>
          <strong>Patient Profile → Get Form QR</strong> — the procedures on the patient's
          appointment are listed with the consent form each one needs, and the form's
          "Teeth #s" field is prefilled with only that procedure's teeth.
        </li>
      </ul>
      <p class="sc-help">
        Forms shown here are pulled from your existing
        <strong>Teamio Forms</strong> library — only forms with type
        <strong>"Other"</strong> appear (configure new ones in Forms first if you need them).
        Add as many rows as you like; multiple rows can reference the same code, and each row
        maps to one form. We deduplicate at send time.
      </p>
      <!-- Forms Settings (release gate 'formsSettings', gate ON only): the one place the procedure mappings and after-care messages are edited. -->
      <p v-if="formsSettingsOn" class="sc-help" data-test="sc-forms-settings-note">
        After-care instructions are set up in Forms Settings, under Procedure mappings.
        <a href="#" class="sc-link" data-test="sc-open-forms-settings" @click.prevent="openFormsSettings">Open Forms Settings</a>
      </p>
    </div>

    <div class="sc-card">
      <div class="sc-rule-table">
        <div class="sc-rule-header">
          <div class="col-codes">Procedure codes</div>
          <div class="col-form">Consent form</div>
          <div class="col-action"></div>
        </div>

        <div v-if="rows.length === 0" class="sc-empty">
          No mappings yet. Click "Add Mapping" below to start.
        </div>

        <div
          v-for="(rule, index) in rows"
          :key="index"
          class="sc-rule-row"
          :class="{ 'sc-rule-row--postop': postOnly(rule) }"
        >
          <div class="col-codes">
            <multiselect
              :value="codeOptionsForRow(rule)"
              :options="procCodesList"
              :multiple="true"
              :searchable="true"
              :close-on-select="false"
              :disabled="postOnly(rule)"
              :show-labels="false"
              :loading="!procCodesList.length"
              label="text"
              track-by="value"
              placeholder="Pick procedure codes…"
              class="sc-multiselect"
              @input="(v) => onCodesChange(index, v)"
            />
          </div>
          <div class="col-form">
            <multiselect
              :value="formOptionForRow(rule)"
              :options="formsOptions"
              :disabled="postOnly(rule)"
              :searchable="true"
              :show-labels="false"
              :loading="loadingForms"
              label="formName"
              track-by="formIDForWeb"
              :placeholder="loadingForms ? 'Loading forms…' : 'Pick a consent form…'"
              class="sc-multiselect"
              @input="(v) => onFormChange(index, v)"
            />
          </div>
          <div class="col-action">
            <button class="sc-remove-btn" type="button" :disabled="postOnly(rule)" @click="removeRow(index)" title="Remove this mapping">
              <i class="fa fa-times"></i>
            </button>
          </div>
          <!-- A row with a post-op form and NO consent form is edited in Forms (the post-op column lives there). Kept on save, never deleted here. -->
          <div v-if="postOnly(rule)" class="sc-postop-note" data-test="sc-postop-only">Post-op only &mdash; edit in Forms<template v-if="formsSettingsOn"> Settings</template></div>
        </div>

        <button class="sc-add-btn" type="button" @click="addRow">
          <i class="fa fa-plus"></i> Add Mapping
        </button>
      </div>

      <div v-if="formsLoadError" class="sc-error">
        <i class="fa fa-exclamation-circle"></i>
        Couldn't load your Teamio Forms list — {{ formsLoadError }}
      </div>
    </div>

    <div class="sc-savebar">
      <!-- Status reads left-to-right through the save: saving → error → saved → dirty.
           A bare spinner on a fast save can flash by unnoticed, so the button label
           changes too and a short-lived confirmation follows. -->
      <span v-if="isSaving" class="sc-saving"><i class="fa fa-circle-o-notch fa-spin"></i> Saving…</span>
      <span v-else-if="saveError" class="sc-save-error">
        <i class="fa fa-exclamation-triangle"></i> {{ saveError }}
      </span>
      <span v-else-if="justSaved" class="sc-saved"><i class="fa fa-check"></i> Saved</span>
      <span v-else-if="dirty" class="sc-dirty">Unsaved changes</span>
      <span v-else class="sc-clean">All changes saved</span>
      <span class="sc-spacer"></span>
      <button class="sc-btn" type="button" @click="onReset" :disabled="!dirty || isSaving">Reset</button>
      <button class="sc-btn-primary" type="button" @click="onSave" :disabled="!dirty || isSaving">
        <i v-if="isSaving" class="fa fa-circle-o-notch fa-spin"></i>
        {{ isSaving ? 'Saving…' : 'Save' }}
      </button>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapState, mapGetters } from 'vuex';
import Multiselect from 'vue-multiselect';
import { TX_PRESENTER_DEFAULTS } from '@/pages/TxPresenter/constants/settingsDefaults.js';
import { cleanRowsForSave, isPostOpOnly, withConsentForm } from '@/utils/postOp/rows';
import { formsSettingsUiOn } from '@/utils/formsSettings/gate';

const cloneDefaults = () => JSON.parse(JSON.stringify(TX_PRESENTER_DEFAULTS.consent));

/**
 * Slice 11 Phase A — Settings tab for mapping procedure codes to consent
 * forms. UI deliberately mirrors the Workflow2 SMS Rules screen so offices
 * recognize the pattern: multi-select code chips + single-select form
 * dropdown per row, "+ Add Mapping" at the bottom.
 *
 * Persistence flow goes through the existing `saveSettingsSection` action
 * (section='consent') which hits the generic `/save-tx-settings` endpoint.
 * No new save-side backend code was needed — only the new
 * `/get-forms-for-consent-mapping` read endpoint to populate the form
 * dropdown.
 */
export default {
  name: 'SettingsConsent',
  components: { Multiselect },
  props: {
    loading: { type: Boolean, default: false },
    saving: { type: Boolean, default: false },
  },
  data() {
    return {
      // Local working copy of the settings section. Bound to the multiselects
      // through getter/setter helpers below so we can convert between the
      // FE multiselect option-object shape ({ value, text } / { formIDForWeb,
      // formName }) and the persisted shape (codeNum/formID arrays).
      local: cloneDefaults(),
      original: cloneDefaults(),

      // Forms list, fetched from /get-forms-for-consent-mapping on mount.
      formsOptions: [],
      loadingForms: false,
      formsLoadError: '',

      // Save feedback. `localSaving` makes the indicator work even when a parent
      // doesn't pass the `saving` prop; `justSaved` is a short-lived confirmation
      // so a fast save still registers; `saveError` surfaces a failure that used to
      // be swallowed entirely (the catch below was empty).
      localSaving: false,
      justSaved: false,
      saveError: '',
      savedTimer: null,
    };
  },
  computed: {
    ...mapState('txPresenter', { settingsState: (s) => s.settings }),
    ...mapGetters('txPresenter', ['settingsConsentResolved']),
    /**
     * The Forms Settings release gate (uiVersion.js area 'formsSettings'). Off ⇒ the classic wording ("edit in Forms"); on ⇒ "edit in Forms
     * Settings" plus the "Open Forms Settings" link. Read on mount (a Super Admin flips it on the Forms page, not here).
     */
    formsSettingsOn() {
      const customer = (this.$store && this.$store.state && this.$store.state.customer) || {};
      const user = customer.user;
      return formsSettingsUiOn({ isSuperAdmin: !!(user && user.Role === 'Super Admin'), userKey: customer.userID });
    },
    /**
     * Procedure codes from the existing customer store (already loaded on
     * app boot). Same shape Workflow2's SMS Rules screen uses — `value` is
     * stringified CodeNum so multiselect tracking matches whatever shape
     * comes back from the persisted JSON.
     */
    procCodesList() {
      const codes = (this.$store.state.customer && this.$store.state.customer.procCodes) || [];
      if (!Array.isArray(codes)) return [];
      return codes.map((c) => ({
        value: String(c.CodeNum || c.ProcCode),
        text: `${c.ProcCode} - ${c.Descript || ''}`.trim(),
      }));
    },
    siteID() { return this.settingsState.siteID; },
    /**
     * Saving from either source: the `saving` prop (TxPresenterSettings and the Forms
     * ConsentMappingModal both pass it from store state) OR our own local flag. The
     * local flag means the indicator can't silently disappear just because a future
     * mount forgets the prop — which is exactly what happened in the Forms modal.
     */
    isSaving() { return this.saving || this.localSaving; },
    dirty() {
      return JSON.stringify(this.local) !== JSON.stringify(this.original);
    },
    rows() {
      // Defensive — `mappings` should always exist from default but guard anyway
      return Array.isArray(this.local.mappings) ? this.local.mappings : [];
    },
  },
  watch: {
    settingsConsentResolved: {
      immediate: true,
      handler(resolved) {
        if (!resolved) return;
        const merged = JSON.parse(JSON.stringify(resolved));
        if (!Array.isArray(merged.mappings)) merged.mappings = [];
        this.local = merged;
        this.original = JSON.parse(JSON.stringify(merged));
      },
    },
    siteID: {
      immediate: true,
      handler(s) {
        if (!s) return;
        this.loadForms();
        this.ensureProcCodes();
      },
    },
  },
  beforeDestroy() {
    // The "Saved" confirmation is on a timer; don't let it fire into a dead component
    // (closing the Forms modal right after saving would otherwise warn).
    clearTimeout(this.savedTimer);
  },
  methods: {
    /** Gate ON: to the Forms page's Settings editor, opened on Procedure mappings (/app/forms?settings=mappings). */
    openFormsSettings() {
      if (this.$router && typeof this.$router.push === 'function') {
        this.$router.push({ name: 'Forms', query: { settings: 'mappings' } });
      }
    },

    /**
     * Populate the procedure-code dropdown.
     *
     * `customer.procCodes` is NOT loaded on app init — only Campaigns, Huddle,
     * PracticePulse and Settings dispatch `getProcCodes`, and the result is then
     * cached in localStorage. So on a fresh browser profile this screen showed an
     * empty list stuck behind `:loading="!procCodesList.length"` forever. That bites
     * hardest on a Forms-only site reaching this screen from Forms → 🔗, which may
     * never visit any of those pages. Load it here rather than depending on where
     * the user happened to browse first. No-op when already cached.
     */
    async ensureProcCodes() {
      const cached = (this.$store.state.customer && this.$store.state.customer.procCodes) || [];
      if (Array.isArray(cached) && cached.length > 0) return;
      try {
        await this.$store.dispatch('customer/getProcCodes', { siteID: this.siteID });
      } catch (e) {
        console.warn('[SettingsConsent] failed to load procedure codes:', e.message);
      }
    },

    async loadForms() {
      if (!this.siteID) return;
      this.loadingForms = true;
      this.formsLoadError = '';
      try {
        const response = await axios.post('/get-forms-for-consent-mapping', { siteID: this.siteID }, { crossdomain: true });
        if (response.data && response.data.success) {
          this.formsOptions = response.data.data.forms || [];
        } else {
          this.formsLoadError = (response.data && response.data.message) || 'Unknown error';
        }
      } catch (e) {
        console.warn('[SettingsConsent] failed to load forms:', e.message);
        this.formsLoadError = e.message || 'Network error';
      } finally {
        this.loadingForms = false;
      }
    },

    /**
     * Bidirectional helper: turn the persisted `codes: [...]` array into
     * the multiselect's expected option-object array (so chips render with
     * the friendly description).
     */
    codeOptionsForRow(rule) {
      const stored = (rule && rule.codes) || [];
      const ids = new Set(stored.map((c) => String(c)));
      return this.procCodesList.filter((opt) => ids.has(opt.value));
    },
    onCodesChange(index, value) {
      const codes = (value || []).map((opt) => opt.value);
      this.$set(this.local.mappings, index, {
        ...this.local.mappings[index],
        codes,
      });
    },

    /**
     * Same idea for the form dropdown. The persisted shape is
     * `formIDs: [formIDForWeb]` (single-element array per the Phase A
     * decision). UI presents single-select; we convert in/out here.
     */
    formOptionForRow(rule) {
      const ids = (rule && rule.formIDs) || [];
      if (!ids.length) return null;
      return this.formsOptions.find((f) => f.formIDForWeb === ids[0]) || null;
    },
    onFormChange(index, value) {
      // withConsentForm keeps every other key on the row (label / postOp / review) and answers the seeder's "Pick a form" flag
      const formID = value && value.formIDForWeb ? value.formIDForWeb : '';
      this.$set(this.local.mappings, index, withConsentForm(this.local.mappings[index], formID));
    },

    /** A post-op-only row (post-op form, no consent form): shown greyed and left alone here. Edited in Forms. */
    postOnly(rule) {
      return isPostOpOnly(rule);
    },

    addRow() {
      this.local.mappings = [
        ...this.local.mappings,
        { codes: [], formIDs: [] },
      ];
    },
    removeRow(index) {
      const list = this.local.mappings.slice();
      list.splice(index, 1);
      this.local.mappings = list;
    },

    async onSave() {
      // Drop rows that are obviously incomplete — one rule for BOTH editors (utils/postOp/rows.js cleanRowsForSave): keep a row
      // iff it has a code AND a consent form OR a post-op form. The old rule here (consent form required) silently deleted every
      // post-op-only row on save, and rebuilding rows lost label / postOp / review — both are kept now.
      const cleaned = cleanRowsForSave(this.local.mappings);
      const payload = { mappings: cleaned };

      this.localSaving = true;
      this.saveError = '';
      this.justSaved = false;
      clearTimeout(this.savedTimer);

      try {
        await this.$store.dispatch('txPresenter/saveSettingsSection', {
          section: 'consent',
          value: payload,
        });
        // Reflect the cleaned list back into local + original so the dirty
        // indicator clears and the row count matches what was saved.
        this.local = JSON.parse(JSON.stringify(payload));
        this.original = JSON.parse(JSON.stringify(payload));

        this.justSaved = true;
        this.savedTimer = setTimeout(() => { this.justSaved = false; }, 2500);
      } catch (e) {
        // Previously swallowed. A failed save left the row list looking unsaved with
        // no explanation — indistinguishable from "nothing happened", which is the
        // very confusion this whole change is about.
        this.saveError = (e && e.message) || 'Could not save — please try again.';
      } finally {
        this.localSaving = false;
      }
    },
    onReset() {
      this.saveError = '';
      this.justSaved = false;
      this.local = JSON.parse(JSON.stringify(this.original));
    },
  },
};
</script>

<style scoped>
.sc {
  max-width: 900px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, system-ui, sans-serif;
}
.sc-section-head { margin-bottom: 22px; }
.sc-section-head h2 {
  margin: 0 0 4px;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: -0.3px;
  color: #0f172a;
}
.sc-help {
  margin: 0 0 8px;
  font-size: 13px;
  color: #64748b;
  line-height: 1.55;
}
.sc-help strong { color: #1f2937; font-weight: 600; }
.sc-link { color: #0f7259; font-weight: 600; text-decoration: none; }
.sc-link:hover { text-decoration: underline; color: #0b5c49; }

.sc-card {
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 18px 20px;
  margin-bottom: 20px;
}

.sc-rule-table { display: flex; flex-direction: column; gap: 10px; }
.sc-rule-header,
.sc-rule-row {
  display: grid;
  /* minmax(0, 1fr), NOT 1fr. `1fr` is shorthand for `minmax(auto, 1fr)`, and that
     `auto` floor means a column can never shrink below its content's min-content
     width. The header and every row are SEPARATE grid containers, so each one sized
     its first column to its own longest chip — e.g. "D7140 - extraction, erupted
     tooth or exposed root (elevation and/or forceps removal)" — and no two rows (nor
     the header) lined up. minmax(0, …) lets the columns ignore content width and
     always split the available space identically. */
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) 32px;
  gap: 12px;
  align-items: start;
}

/* Long option labels must ellipsize rather than push the column wider. */
.sc-multiselect /deep/ .multiselect__tag,
.sc-multiselect /deep/ .multiselect__single {
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.sc-rule-header {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  color: #6b7785;
  padding: 0 2px 4px;
  border-bottom: 1px solid #f1f5f9;
}
.sc-empty {
  padding: 16px 8px;
  font-size: 13px;
  color: #94a3b8;
  font-style: italic;
}
.sc-rule-row {
  padding: 6px 0;
}

.sc-multiselect {
  font-size: 13px;
}
.sc-multiselect /deep/ .multiselect__tags { min-height: 38px; }
.sc-multiselect /deep/ .multiselect__tag {
  background: #0d5f54;
  color: #fff;
  border-radius: 4px;
}
.sc-multiselect /deep/ .multiselect__tag-icon:after { color: #fff; }
.sc-multiselect /deep/ .multiselect__option--highlight { background: #0d5f54; }
.sc-multiselect /deep/ .multiselect__option--selected.multiselect__option--highlight { background: #0a4a42; }

.sc-rule-row--postop { opacity: 0.6; }
.sc-postop-note {
  grid-column: 1 / -1;
  font-size: 12px;
  color: #64748b;
}
.sc-remove-btn:disabled { cursor: not-allowed; }
.sc-remove-btn {
  width: 32px;
  height: 32px;
  margin-top: 3px;
  border: 1px solid #e5e7eb;
  border-radius: 4px;
  background: #fff;
  color: #94a3b8;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sc-remove-btn:hover {
  border-color: #fca5a5;
  color: #dc2626;
  background: #fef2f2;
}

.sc-add-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  padding: 8px 14px;
  background: #f8fafc;
  border: 1px dashed #cbd5e1;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  color: #0d5f54;
  cursor: pointer;
  transition: all 0.15s ease;
  align-self: flex-start;
}
.sc-add-btn:hover {
  background: #f0f7f6;
  border-color: #0d5f54;
}

.sc-error {
  margin-top: 12px;
  padding: 10px 12px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 6px;
  font-size: 12px;
  color: #991b1b;
}
.sc-error .fa { margin-right: 6px; color: #dc2626; }

.sc-savebar {
  display: flex;
  align-items: center;
  gap: 10px;
  padding-top: 12px;
  border-top: 1px solid #e5e7eb;
}
.sc-spacer { flex: 1; }
.sc-dirty { font-size: 12px; color: #d97706; font-weight: 500; }
.sc-clean { font-size: 12px; color: #16a34a; }
.sc-saving { font-size: 12px; color: #6b7785; font-weight: 500; }
.sc-saved { font-size: 12px; color: #16a34a; font-weight: 600; }
.sc-save-error { font-size: 12px; color: #dc2626; font-weight: 500; }
.sc-saving i,
.sc-saved i,
.sc-save-error i { margin-right: 4px; }
.sc-btn,
.sc-btn-primary {
  padding: 8px 16px;
  font-size: 13px;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}
.sc-btn {
  background: #fff;
  border: 1px solid #d4d8de;
  color: #475569;
}
.sc-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sc-btn:hover:not(:disabled) { background: #f8fafc; border-color: #94a3b8; }
.sc-btn-primary {
  background: #0d5f54;
  border: 1px solid #0d5f54;
  color: #fff;
}
.sc-btn-primary:disabled { opacity: 0.5; cursor: not-allowed; }
.sc-btn-primary:hover:not(:disabled) { background: #0a4a42; }
.sc-btn-primary .fa { margin-right: 4px; }
</style>
